<script setup>
import { useDialogManager } from '@0x234/common-vue/composables/dialog-manager';
import UserProfilePhoto from 'components/UserProfilePhoto.vue';
import AppRoute from 'router/app-route';
import { Route } from 'router/routes';
import { useAuthStore } from 'stores/auth-store';
import { useSiteContextStore } from 'stores/site-context-store';
import { computed, defineAsyncComponent, onUnmounted, ref } from 'vue';

const authStore = useAuthStore();
const dialogManager = useDialogManager();
const siteContextStore = useSiteContextStore();
const isEmailMasked = ref(true);

const currentSiteSwitcherLabel = computed(() => {
    return siteContextStore.isEmployerSiteContext
        ? 'Go to Job Seekers Site'
        : 'Go to Employers Site';
});

const currentSiteSwitcherRoute = computed(() => {
    return siteContextStore.isEmployerSiteContext
        ? AppRoute.build(Route.LandingPage)
        : AppRoute.build(Route.EmployerLandingPage);
});

onUnmounted(() => {
    isEmailMasked.value = true;
});

function toggleEmailMask() {
    isEmailMasked.value = !isEmailMasked.value;
}

function maskEmail(email) {
    if (!email || !isEmailMasked.value) {
        return email;
    }
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) {
        return email;
    }
    if (localPart.length <= 2) {
        return localPart[0] + '*' + (localPart.length > 1 ? localPart[localPart.length - 1] : '') + '@' + domain;
    }
    return localPart[0] + '*'.repeat(localPart.length - 2) + localPart[localPart.length - 1] + '@' + domain;
}

function openLogoutDialog() {
    dialogManager.openDialogComponent(defineAsyncComponent(() => import('components/UserLogoutDialog.vue')));
}
</script>

<template>
    <q-list class="user-menu-list">
        <!-- User Profile Section -->
        <q-item-label class="text-subtitle2 q-px-md q-pt-md text-weight-bold" header>
            <template v-if="authStore.isUserAuthenticated">
                Your Account
            </template>
            <template v-else>
                Welcome
            </template>
        </q-item-label>

        <template v-if="authStore.isUserAuthenticated">
            <q-item class="q-py-md user-profile-item">
                <q-item-section avatar>
                    <UserProfilePhoto bordered/>
                </q-item-section>
                <q-item-section>
                    <q-item-label class="text-weight-bold">{{ authStore.user.displayName ?? 'New User' }}</q-item-label>
                    <q-item-label caption class="cursor-pointer" @click.capture.stop="toggleEmailMask">
                        {{ maskEmail(authStore.user.email) }}
                    </q-item-label>
                </q-item-section>
            </q-item>

            <q-item v-ripple :to="AppRoute.build(Route.ProfileManagerPage)" clickable>
                <q-item-section avatar>
                    <q-icon color="primary" name="sym_r_badge" size="sm"/>
                </q-item-section>
                <q-item-section>
                    <q-item-label>My Profile</q-item-label>
                </q-item-section>
            </q-item>
        </template>
        <template v-else>
            <q-item v-ripple :to="AppRoute.build(Route.LoginPage)" clickable>
                <q-item-section avatar>
                    <q-icon color="primary" name="sym_r_login" size="sm"/>
                </q-item-section>
                <q-item-section>
                    <q-item-label class="text-weight-bold text-primary">Login / Register</q-item-label>
                </q-item-section>
            </q-item>
        </template>

        <q-separator class="q-my-sm gradient-separator"/>

        <!-- Site-Specific Sections -->
        <q-item-label class="text-subtitle2 q-px-md text-weight-bold" header>
            {{ siteContextStore.isEmployerSiteContext ? 'Employer Tools' : 'Job Seeker Tools' }}
        </q-item-label>

        <!-- Job Seeker Section -->
        <template v-if="!siteContextStore.isEmployerSiteContext">
            <q-item v-ripple :to="AppRoute.build(Route.SearchResultsPage)" clickable>
                <q-item-section avatar>
                    <q-icon name="sym_r_search" size="sm"/>
                </q-item-section>
                <q-item-section>
                    <q-item-label>Search Jobs</q-item-label>
                </q-item-section>
            </q-item>

            <template v-if="authStore.isUserAuthenticated">
                <q-item v-ripple :to="AppRoute.build(Route.ShortlistPage)" clickable>
                    <q-item-section avatar>
                        <q-icon name="sym_r_star" size="sm"/>
                    </q-item-section>
                    <q-item-section>
                        <q-item-label>Saved Jobs</q-item-label>
                    </q-item-section>
                </q-item>

                <q-item v-ripple :to="AppRoute.build(Route.JobApplicationPage)" clickable>
                    <q-item-section avatar>
                        <q-icon name="sym_r_description" size="sm"/>
                    </q-item-section>
                    <q-item-section>
                        <q-item-label>My Applications</q-item-label>
                    </q-item-section>
                </q-item>
            </template>
        </template>

        <!-- Employer Section -->
        <template v-else>
            <q-item v-ripple :to="AppRoute.build(Route.JobManagerWizardPage)" clickable>
                <q-item-section avatar>
                    <q-icon name="sym_r_add_circle" size="sm"/>
                </q-item-section>
                <q-item-section>
                    <q-item-label>Post a Job</q-item-label>
                </q-item-section>
            </q-item>

            <template v-if="authStore.user?.totalJobs > 0">
                <q-item v-ripple :to="AppRoute.build(Route.JobManagerListPage)" clickable>
                    <q-item-section avatar>
                        <q-icon name="sym_r_work" size="sm"/>
                    </q-item-section>
                    <q-item-section>
                        <q-item-label>My Jobs</q-item-label>
                    </q-item-section>
                </q-item>
            </template>

            <template v-if="authStore.user?.totalCompanies > 0">
                <q-item v-ripple :to="AppRoute.build(Route.CompanyManagerPage)" clickable>
                    <q-item-section avatar>
                        <q-icon name="sym_r_business" size="sm"/>
                    </q-item-section>
                    <q-item-section>
                        <q-item-label>My Companies</q-item-label>
                    </q-item-section>
                </q-item>
            </template>
        </template>

        <!-- Settings Section (if authenticated) -->
        <template v-if="authStore.isUserAuthenticated">
            <q-separator class="q-my-sm gradient-separator"/>

            <q-item-label class="text-subtitle2 q-px-md text-weight-bold" header>
                Settings
            </q-item-label>

            <q-item v-ripple :to="AppRoute.build(Route.AccountSettingsPage)" clickable>
                <q-item-section avatar>
                    <q-icon name="sym_r_settings" size="sm"/>
                </q-item-section>
                <q-item-section>
                    <q-item-label>Account Settings</q-item-label>
                </q-item-section>
            </q-item>

            <q-item v-ripple :to="AppRoute.build(Route.AccountSecurityPage)" clickable>
                <q-item-section avatar>
                    <q-icon name="sym_r_security" size="sm"/>
                </q-item-section>
                <q-item-section>
                    <q-item-label>Security</q-item-label>
                </q-item-section>
            </q-item>
        </template>

        <!-- Site Switcher -->
        <q-separator class="q-my-sm gradient-separator"/>

        <q-item v-ripple :to="currentSiteSwitcherRoute" class="site-switcher-item" clickable>
            <q-item-section avatar>
                <q-icon color="primary" name="sym_r_swap_horiz" size="sm"/>
            </q-item-section>
            <q-item-section>
                <q-item-label class="text-primary">{{ currentSiteSwitcherLabel }}</q-item-label>
            </q-item-section>
        </q-item>

        <!-- Logout Button (if authenticated) -->
        <template v-if="authStore.isUserAuthenticated">
            <q-separator class="q-my-sm gradient-separator"/>
            <q-item v-ripple clickable @click="openLogoutDialog()">
                <q-item-section avatar>
                    <q-icon color="negative" name="sym_r_logout" size="sm"/>
                </q-item-section>
                <q-item-section>
                    <q-item-label class="text-negative">Log Out</q-item-label>
                </q-item-section>
            </q-item>
        </template>
    </q-list>
</template>

<style lang="scss" scoped>
.user-menu-list {
    border-radius: 8px;
    min-width: 280px;
    overflow: hidden;

    .q-item {
        min-height: 48px;
        padding: 8px 16px;

        &.user-profile-item {
            padding-bottom: 12px;
            padding-top: 8px;
        }
    }

    .q-item:hover {
        background-color: rgba(var(--q-primary), 0.05);
    }

    .q-item.router-link-active {
        background-color: rgba(var(--q-primary), 0.1);
        font-weight: 500;
    }

    .q-separator {
        background-color: rgba(0, 0, 0, 0.08);

        .body--dark & {
            background-color: rgba(255, 255, 255, 0.08);
        }
    }

    .q-item-label.header {
        color: var(--q-hint);
        font-size: 13px;
        letter-spacing: 0.5px;
        padding-bottom: 4px;
        padding-top: 12px;
        text-transform: uppercase;

        .body--dark & {
            color: rgba(255, 255, 255, 0.7);
        }
    }

    .site-switcher-item {
        transition: background-color 0.2s;

        &:hover {
            background-color: rgba(var(--q-primary), 0.1);
        }
    }

    .body--dark & {
        .q-item:hover {
            background-color: rgba(255, 255, 255, 0.1);
        }

        .q-item.router-link-active {
            background-color: rgba(var(--q-primary), 0.25);
        }
    }

    // Special styles for when the list is in a dialog
    &.in-dialog {
        .q-item {
            background-color: transparent !important;
            border-radius: 8px;
            margin: 2px 8px;

            &:hover {
                background-color: rgba(var(--q-primary), 0.05) !important;
            }

            &.router-link-active {
                background-color: rgba(var(--q-primary), 0.1) !important;
            }
        }

        .body--dark & .q-item {
            &:hover {
                background-color: rgba(255, 255, 255, 0.1) !important;
            }

            &.router-link-active {
                background-color: rgba(var(--q-primary), 0.25) !important;
            }
        }
    }
}
</style>
